import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CameraOff, Eye, Loader2, Radio } from 'lucide-react'
import { fmtNumber } from '@/shared/lib/format'
import { stop, useBroadcastSession, useElapsed } from './useBroadcast'
import { fmtClock } from './players'

/**
 * You are on air, everywhere in the app.
 *
 * The broadcast session survives navigation now, which is what somebody running
 * a rally actually needs — glance at the diary, check who is watching, come back.
 * It also means the one thing that must never happen is somebody forgetting a
 * camera is live, so the state follows them: a bar, always visible, with the stop
 * control in it.
 *
 * Rendered in the app shell rather than on the coverage screens, because the
 * screens are exactly where it is NOT needed — the panel already says ON AIR.
 */
export function OnAirBar() {
  const session = useBroadcastSession()
  const elapsed = useElapsed(session.startedAt)
  const [stopping, setStopping] = useState(false)

  if (session.phase !== 'live' || !session.feedId) return null

  return (
    <div
      role="status"
      className="flex h-9 shrink-0 items-center gap-3 border-b border-red-600/40 bg-red-600/10 px-3 text-erp-xs"
    >
      <span className="flex items-center gap-1.5 font-semibold text-red-700 dark:text-red-400">
        <Radio className="h-3.5 w-3.5" />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" />
        ON AIR
      </span>
      <span className="min-w-0 truncate">
        {session.feedTitle}
        {session.eventTitle && <span className="text-muted-foreground"> · {session.eventTitle}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
        <Eye className="h-3 w-3" /> {fmtNumber(session.viewers)}
      </span>
      <span className="shrink-0 tabular-nums text-muted-foreground">{fmtClock(elapsed)}</span>

      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        <Link
          className="erp-btn-ghost h-7 text-erp-xs text-chocolate"
          to={`/coverage/go-live?feed=${session.feedId}`}
        >
          Open
        </Link>
        <button
          type="button"
          className="erp-btn h-7 border-red-600 text-red-600 hover:bg-red-600/10"
          disabled={stopping}
          onClick={async () => {
            setStopping(true)
            try { await stop() } finally { setStopping(false) }
          }}
        >
          {stopping ? <Loader2 className="h-3 w-3 animate-spin" /> : <CameraOff className="h-3 w-3" />}
          Stop
        </button>
      </span>
    </div>
  )
}
